import { useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { PublicLayout } from "@/components/layout/public-layout";
import { fetchMarketingPages, type MarketingCategory } from "@/lib/public-marketing";
import { MarketingHelmet } from "@/pages/public/marketing/marketing-seo";

function isoToPrettyDate(iso: string | null) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

function dateRange(startIso: string | null, endIso: string | null) {
  const s = isoToPrettyDate(startIso);
  const e = isoToPrettyDate(endIso);
  if (s && e && s !== e) return `${s} – ${e}`;
  return s || e || "";
}

export function PublicMarketingIndexPage({
  category,
  title,
  description,
  pathBase,
}: {
  category: MarketingCategory;
  title: string;
  description: string;
  pathBase: string;
}) {
  const [searchParams] = useSearchParams();
  const page = Math.max(1, Number(searchParams.get("page") || "1") || 1);

  const { data, isLoading, isError } = useQuery({
    queryKey: ["publicMarketingPages", category, page],
    queryFn: () => fetchMarketingPages({ category, page }),
  });

  const canonicalUrl = useMemo(() => {
    const origin = window.location.origin;
    if (page <= 1) return `${origin}${pathBase}`;
    return `${origin}${pathBase}?page=${page}`;
  }, [page, pathBase]);

  return (
    <PublicLayout>
      <MarketingHelmet title={`${title} · DealerDog Finance`} description={description} canonicalUrl={canonicalUrl} />
      <section className="max-w-6xl mx-auto px-4 sm:px-6 pt-8 pb-16">
        <div className="max-w-3xl">
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-gray-900">{title}</h1>
          <p className="mt-3 text-gray-600">{description}</p>
        </div>

        {isLoading ? <p className="mt-10 text-gray-600">Loading…</p> : null}
        {isError ? <p className="mt-10 text-red-600">Failed to load.</p> : null}

        {data ? (
          <>
            <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {data.results.map((p) => {
                const href = p.canonical_path;
                const showDate =
                  category === "TRADESHOWS"
                    ? dateRange(p.event_start_date, p.event_end_date)
                    : isoToPrettyDate(p.published_at);
                return (
                  <Link
                    key={p.id}
                    to={href}
                    className="group rounded-xl border border-gray-200 bg-white overflow-hidden hover:shadow-sm transition-shadow"
                  >
                    {p.cover_image_url ? (
                      <div className="aspect-[16/9] bg-gray-50 overflow-hidden">
                        <img
                          src={p.cover_image_url}
                          alt=""
                          className="h-full w-full object-cover group-hover:scale-[1.01] transition-transform"
                          loading="lazy"
                        />
                      </div>
                    ) : null}
                    <div className="p-5">
                      <div className="text-xs font-medium text-gray-500">{showDate}</div>
                      <div className="mt-2 text-lg font-semibold text-gray-900 group-hover:underline">
                        {p.title}
                      </div>
                      {p.excerpt?.trim() ? (
                        <p className="mt-2 text-sm text-gray-600 line-clamp-3">{p.excerpt}</p>
                      ) : null}
                    </div>
                  </Link>
                );
              })}
            </div>

            <div className="mt-10 flex items-center justify-between">
              <div className="text-sm text-gray-500">
                Page {data.page} of {Math.max(1, Math.ceil(data.count / data.page_size))}
              </div>
              <div className="flex gap-3">
                {data.has_previous ? (
                  <Link
                    className="text-sm font-medium text-gray-700 hover:text-gray-900"
                    to={`${pathBase}?page=${page - 1}`}
                  >
                    Previous
                  </Link>
                ) : (
                  <span className="text-sm text-gray-300">Previous</span>
                )}
                {data.has_next ? (
                  <Link
                    className="text-sm font-medium text-gray-700 hover:text-gray-900"
                    to={`${pathBase}?page=${page + 1}`}
                  >
                    Next
                  </Link>
                ) : (
                  <span className="text-sm text-gray-300">Next</span>
                )}
              </div>
            </div>
          </>
        ) : null}
      </section>
    </PublicLayout>
  );
}

